import { fromJS } from "immutable";

import { canvasInfoActions } from "actions/canvasInfoActions.js";

const initialState = fromJS({
    isLoading: true,
    hasErrors: false,
    data: {
        buyplan_id: null,
        month: null,
        pages: [],
    active_page_id: "",
    // Global flag for hide products on canvas
    hideProductsOnCanvas: false,
    // Map of pageId -> boolean for hideProductsOnCanvas per page
    hideProductsOnCanvasMap: {}
    },
    pagesList: {
        isLoading: false,
        hasErrors: false,
        data: []
    },
    // Separate data for canvas state all (used only by selector canvas)
    canvasStateAll: {
        isLoading: false,
        hasErrors: false,
        data: null
    }
});

export default (state = initialState, action) => {
    switch (action.type) {
        case canvasInfoActions.startLoader:
            return state.set("isLoading", true);
        case canvasInfoActions.reset:
            // Preserve the hideProductsOnCanvas toggle state when resetting
            const currentToggleState = state.getIn(['data', 'hideProductsOnCanvas']);
            const resetState = initialState.setIn(['data', 'hideProductsOnCanvas'], currentToggleState || false);
            return resetState;
        case canvasInfoActions.success:
            // Handle new API response format with current_page, total_pages, and page_titles
            if (action.payload && action.payload.data) {
                // Normalize response shapes. Some endpoints may return a double-wrapped
                // payload like { data: { data: { ... } } } which causes callers to not
                // find `current_page`. Prefer the inner `data` when present.
                let apiData = action.payload.data;
                if (apiData && apiData.data && (apiData.current_page === undefined || apiData.current_page === null)) {
                    apiData = apiData.data;
                }


                const currentPage = apiData.current_page;
                const pageTitles = apiData.page_titles || {};

                // Get existing page titles from state to preserve custom names
                const existingPageTitles = state.getIn(['data', 'page_titles']) || {};
                const existingPageTitlesJS = existingPageTitles.toJS ? existingPageTitles.toJS() : existingPageTitles;
                
                // Filter out page0 if it exists in pageTitles and merge with existing titles
                const filteredPageTitles = { ...existingPageTitlesJS }; // Start with existing titles
                Object.keys(pageTitles).forEach(key => {
                    if (key !== 'page0') {
                        filteredPageTitles[key] = pageTitles[key]; // Override with API response
                    }
                });

                // We no longer need to calculate total pages since we're not creating default pages

                // Get existing pages from state
                const existingPages = state.getIn(['data', 'pages']) || fromJS([]);

                // CRITICAL FIX: Only keep pages that belong to the current month
                // Filter existing pages to only include those that are in the current month's page_titles
                const validPageIds = new Set(Object.keys(filteredPageTitles));
                const filteredExistingPages = existingPages.filter(page => {
                    if (!page || !page.get) return false;
                    const pageId = page.get('id');
                    return validPageIds.has(pageId);
                });

                // Initialize updatedPages with filtered existing pages
                let updatedPages = filteredExistingPages;

                // Handle current_page being null
                if (currentPage === null) {
                    // If current_page is null, we need to rely entirely on page_titles
                    // and preserve the existing pages' canvas states

                    // Determine active page ID - use current active or empty string (no fallback)
                    const activePageId = state.getIn(['data', 'active_page_id']) || '';

                    // Create or update pages based on filteredPageTitles
                    Object.keys(filteredPageTitles).forEach(pageId => {
                        const pageTitle = filteredPageTitles[pageId];
                        // We don't need pageNumber here since we're using the title directly from filteredPageTitles

                        // Check if this page already exists in our state
                        const existingPageIndex = updatedPages.findIndex(
                            p => p && p.get && p.get('id') === pageId
                        );

                        if (existingPageIndex !== -1) {
                            // Update existing page's title but keep its canvas state
                            updatedPages = updatedPages.update(existingPageIndex, page =>
                                page.set('name', pageTitle)
                            );
                        } else {
                            // Add new page with title from filteredPageTitles
                            updatedPages = updatedPages.push(fromJS({
                                id: pageId,
                                name: pageTitle,
                                canvas_state: null
                            }));
                        }
                    });

                    // Don't create default pages - only use pages from filteredPageTitles

                    // Sort pages by their numeric ID
                    // updatedPages = updatedPages.sort((a, b) => {
                    //     const idA = a.get('id');
                    //     const idB = b.get('id');
                    //     const numA = parseInt(idA.replace('page', ''), 10);
                    //     const numB = parseInt(idB.replace('page', ''), 10);
                    //     return numA - numB;
                    // });

                    // Create transformed data with the same active page
                    const transformedData = {
                        buyplan_id: apiData.buyplan_id,
                        month: apiData.month,
                        pages: updatedPages,
                        active_page_id: activePageId,
                        // Keep original current_page (null in this branch) so components
                        // that expect `data.current_page` can handle the shape.
                        current_page: null,
                        hideProductsOnCanvasMap: state.getIn(['data', 'hideProductsOnCanvasMap']) || {},
                        // CRITICAL FIX: Store page_titles and total_pages from API response
                        page_titles: filteredPageTitles,
                        total_pages: apiData.total_pages || Object.keys(filteredPageTitles).length
                    };

                    return state.merge({
                        isLoading: false,
                        hasErrors: false,
                        data: fromJS(transformedData)
                    });
                }

                // If we get here, current_page is not null, so proceed with normal processing
                // Normalize current_page into a plain JS object so we can safely
                // normalize the `page` field and read nested canvas_state paths.
                const plainCurrentPage = (currentPage && typeof currentPage.toJS === 'function')
                    ? currentPage.toJS()
                    : (currentPage || {});

                // Get the canvas state from the current page's nested structure
                const canvasStateData = (plainCurrentPage.canvas_state && (
                    (plainCurrentPage.canvas_state.buyplan_canvas_state && plainCurrentPage.canvas_state.buyplan_canvas_state.canvas_state) ||
                    plainCurrentPage.canvas_state.canvas_state
                )) || plainCurrentPage.canvas_state || {};

                // Normalize the current page id into the string form 'pageX'.
                // Some API responses return a numeric page (e.g. 1). We must
                // convert that to 'page1' to match page_titles and the rest of
                // the app which expects string page ids like 'page1'.
                const rawPageValue = plainCurrentPage.page;
                let currentPageId = '';
                if (rawPageValue === null || rawPageValue === undefined || rawPageValue === '') {
                    currentPageId = '';
                } else if (typeof rawPageValue === 'number' || /^[0-9]+$/.test(String(rawPageValue))) {
                    currentPageId = `page${rawPageValue}`;
                } else if (String(rawPageValue).startsWith('page')) {
                    currentPageId = String(rawPageValue);
                } else {
                    // If it's a string but doesn't start with 'page', prefix it.
                    currentPageId = `page${String(rawPageValue)}`;
                }

                if (currentPageId === 'page0') {
                    currentPageId = '';
                }

                // Use custom page title if it exists in state, otherwise use API response or default
                const currentPageTitle = existingPageTitlesJS[currentPageId] || 
                                       plainCurrentPage.page_title || 
                                       (rawPageValue ? `Page ${rawPageValue}` : '');

                // Create page object for current page (use string id)
                const currentPageObj = fromJS({
                    id: currentPageId,
                    name: currentPageTitle,
                    canvas_state: canvasStateData
                });

                // Find and update the current page
                const currentPageIndex = updatedPages.findIndex(p => p && p.get && p.get('id') === currentPageId);
                if (currentPageIndex !== -1) {
                    // Update existing page
                    updatedPages = updatedPages.set(currentPageIndex, currentPageObj);
                } else {
                    // Add new page
                    updatedPages = updatedPages.push(currentPageObj);
                }

                // Determine which page IDs to create based on filteredPageTitles
                const pageIdsToCreate = [];

                // If we have pageTitles, use those keys to determine which pages to create
                if (Object.keys(filteredPageTitles).length > 0) {
                    Object.keys(filteredPageTitles).forEach(pageId => {
                        if (pageId !== 'page0') { // Extra check to ensure page0 is ignored
                            pageIdsToCreate.push(pageId);
                        }
                    });
                }
                // Don't create default pages if no pageTitles - rely only on API data

                // Create or update pages based on pageIdsToCreate
                pageIdsToCreate.forEach(pageId => {
                    const pageExists = updatedPages.some(p => p && p.get && p.get('id') === pageId);

                    if (!pageExists) {
                        // Extract page number from pageId (e.g., "page1" -> 1)
                        const pageNumber = parseInt(pageId.replace('page', ''), 10);

                        // Create a new page with title from filteredPageTitles if available
                        const pageTitle = filteredPageTitles[pageId] || `Page ${pageNumber}`;
                        updatedPages = updatedPages.push(fromJS({
                            id: pageId,
                            name: pageTitle,
                            canvas_state: null
                        }));
                    } else if (filteredPageTitles[pageId]) {
                        // Update existing page title if it exists in filteredPageTitles
                        const pageIndex = updatedPages.findIndex(p => p && p.get && p.get('id') === pageId);
                        if (pageIndex !== -1) {
                            updatedPages = updatedPages.update(pageIndex, page => page.set('name', filteredPageTitles[pageId]));
                        }
                    }
                });

                // Sort pages by their numeric ID (disabled - ordering not required here)

                // Normalize and attach a consistent `current_page` object where
                // `page` is always the string form (e.g. 'page1'). This helps
                // FabricCanvas and other consumers access nested canvas_state
                // reliably.
                const normalizedCurrentPage = Object.assign({}, plainCurrentPage, { page: currentPageId });

                const transformedData = {
                    buyplan_id: apiData.buyplan_id || normalizedCurrentPage.buyplan_id,
                    month: apiData.month || normalizedCurrentPage.month,
                    pages: updatedPages,
                    active_page_id: currentPageId,
                    // Attach normalized current_page for consumers
                    current_page: normalizedCurrentPage,
                    // Preserve global toggle state
                    hideProductsOnCanvas: state.getIn(['data', 'hideProductsOnCanvas']) || false,
                    hideProductsOnCanvasMap: state.getIn(['data', 'hideProductsOnCanvasMap']) || {},
                    // CRITICAL FIX: Store page_titles and total_pages from API response
                    page_titles: filteredPageTitles,
                    total_pages: apiData.total_pages || Object.keys(filteredPageTitles).length
                };

                return state.merge({
                    isLoading: false,
                    hasErrors: false,
                    data: fromJS(transformedData)
                });
            }

            // Don't add legacy data handling with hardcoded page1

            // Fallback: try to be resilient to different payload shapes
            const fallbackData = (action.payload && action.payload.data && action.payload.data.data)
                ? action.payload.data.data
                : action.payload && action.payload.data
                    ? action.payload.data
                    : {};

            // Preserve toggle state in fallback case
            const fallbackDataWithToggle = {
                ...fallbackData,
                hideProductsOnCanvas: state.getIn(['data', 'hideProductsOnCanvas']) || false
            };
            
            return state.merge({
                isLoading: false,
                hasErrors: false,
                data: fromJS(fallbackDataWithToggle)
            });
        case canvasInfoActions.failure:
            return state.merge({
                isLoading: false,
                hasErrors: true,
                data: fromJS(action.payload.data)
            });
        case canvasInfoActions.addPage:
            try {
                const { pageName, pageId: providedPageId } = action.payload;

                // Safely check if state has proper structure
                let stateWithPageCheck = state;
                let pageCount = 0;

                try {
                    // Make sure state.data.pages exists and is an Immutable.js List
                    if (state && typeof state.getIn === 'function') {
                        const pages = state.getIn(['data', 'pages']);
                        if (!pages || typeof pages.size !== 'number' || pages.size === 0) {
                            // Initialize with empty pages array
                            stateWithPageCheck = state.setIn(['data', 'pages'], fromJS([]));
                        } else {
                            stateWithPageCheck = state;
                        }

                        // Safely get the page count
                        const safePages = stateWithPageCheck.getIn(['data', 'pages']);
                        if (safePages && typeof safePages.size === 'number') {
                            pageCount = safePages.size;
                        }
                    } else {
                        // If state doesn't have getIn method, reconstruct a valid state
                        stateWithPageCheck = fromJS({
                            isLoading: false,
                            hasErrors: false,
                            data: {
                                buyplan_id: null,
                                month: null,
                                pages: [],
                                active_page_id: '',
                                hideProductsOnCanvas: state.getIn(['data', 'hideProductsOnCanvas']) || false
                            }
                        });
                        pageCount = 1;
                    }
                } catch (error) {
                    // If anything fails, fall back to a safe default state
                    stateWithPageCheck = fromJS({
                        isLoading: false,
                        hasErrors: false,
                        data: {
                            buyplan_id: null,
                            month: null,
                            pages: [],
                            active_page_id: '',
                            hideProductsOnCanvas: state.getIn(['data', 'hideProductsOnCanvas']) || false
                        }
                    });
                    pageCount = 1;
                }

                // Compute new page id only if not provided
                const computedPageId = `page${pageCount + 1}`;
                const newPageId = providedPageId || computedPageId;
                const newPageName = pageName || `Page ${pageCount + 1}`;

                try {
                    // Add the new page to data.pages and set active page
                    let newState = stateWithPageCheck.updateIn(['data', 'pages'], pages =>
                        pages.push(fromJS({
                            id: newPageId,
                            name: newPageName,
                            canvas_state: null
                        }))
                    ).setIn(['data', 'active_page_id'], newPageId);

                    // Also mirror the new page into pagesList.data.page_titles so
                    // the tabs UI (which reads page_titles) will show the new tab
                    // immediately and the edit input can render.
                    try {
                        const pageTitlesPath = ['pagesList', 'data', 'page_titles', newPageId];
                        // Ensure pagesList.data exists
                        if (!newState.getIn(['pagesList', 'data'])) {
                            newState = newState.setIn(['pagesList', 'data'], fromJS({}));
                        }
                        // If page_titles is not a map yet, initialize it
                        if (!newState.getIn(['pagesList', 'data', 'page_titles'])) {
                            newState = newState.setIn(['pagesList', 'data', 'page_titles'], fromJS({}));
                        }
                        newState = newState.setIn(pageTitlesPath, newPageName);
                    } catch (mirrorErr) {
                        // ignore mirroring errors and proceed with newState
                    }

                    return newState;
                } catch (updateError) {
                    // If updating fails, rebuild the entire state manually
                    const currentPages = stateWithPageCheck.getIn(['data', 'pages']) || fromJS([]);
                    const newPages = currentPages.concat(fromJS([{
                        id: newPageId,
                        name: newPageName,
                        canvas_state: null
                    }]));

                    return fromJS({
                        isLoading: stateWithPageCheck.get('isLoading') || false,
                        hasErrors: stateWithPageCheck.get('hasErrors') || false,
                        data: {
                            buyplan_id: stateWithPageCheck.getIn(['data', 'buyplan_id']) || null,
                            month: stateWithPageCheck.getIn(['data', 'month']) || null,
                            pages: newPages.toJS(),
                            active_page_id: newPageId,
                            hideProductsOnCanvas: stateWithPageCheck.getIn(['data', 'hideProductsOnCanvas']) || false
                        }
                    });
                }
            } catch (error) {
                // Last resort fallback - return state unchanged
                return state;
            }

        case canvasInfoActions.removePage:
            try {
                const { pageId: pageToRemove } = action.payload;

                // Safely check if we can remove pages
                if (!state || typeof state.getIn !== 'function') {
                    return state;
                }

                try {
                    const pages = state.getIn(['data', 'pages']);

                    // Don't remove the last page
                    if (!pages || typeof pages.size !== 'number' || pages.size <= 1) {
                        return state;
                    }

                    // If removing the active page, switch to the first page safely
                    let updatedState = state;

                    try {
                        const activePageId = state.getIn(['data', 'active_page_id']);

                        if (activePageId === pageToRemove) {
                            try {
                                // Try to get the first page ID. If not found, clear active page
                                let firstPageId = '';

                                const firstPage = pages && typeof pages.first === 'function' ? pages.first() : null;
                                if (firstPage && typeof firstPage.get === 'function') {
                                    const possibleFirstId = firstPage.get('id');
                                    if (possibleFirstId) {
                                        firstPageId = possibleFirstId;
                                    }
                                }

                                updatedState = state.setIn(['data', 'active_page_id'], firstPageId);
                            } catch (activePageError) {
                                // If setting active page fails, just continue with removal
                                updatedState = state;
                            }
                        }
                    } catch (activeCheckError) {
                        // If checking active page fails, just continue with original state
                        updatedState = state;
                    }

                    // Safely remove the page
                    try {
                        return updatedState.updateIn(['data', 'pages'], pages =>
                            pages.filter(page => {
                                // Safe filtering that won't throw if page structure is unexpected
                                try {
                                    return page && typeof page.get === 'function' &&
                                        page.get('id') !== pageToRemove;
                                } catch (filterError) {
                                    // Keep this page if we can't properly check it
                                    return true;
                                }
                            })
                        );
                    } catch (removeError) {
                        // If page removal fails, return the state unchanged
                        return updatedState;
                    }
                } catch (pagesError) {
                    // If anything fails during page access, return state unchanged
                    return state;
                }
            } catch (outerError) {
                // Last resort fallback for any unexpected errors
                return state;
            }

        case canvasInfoActions.renamePage:
            try {
                const { pageId: renamePageId, pageName: newPageName } = action.payload;

                // First check if state is a valid Immutable object
                if (!state || typeof state.getIn !== 'function') {
                    return state;
                }

                try {
                    const pagesForRename = state.getIn(['data', 'pages']);
                    if (!pagesForRename || typeof pagesForRename.size !== 'number' || pagesForRename.size === 0) {
                        return state;
                    }

                    try {
                        // Find the page to rename with additional safety checks
                        let pageIndex = -1;

                        try {
                            if (typeof pagesForRename.findIndex === 'function') {
                                pageIndex = pagesForRename.findIndex(
                                    page => page && typeof page.get === 'function' && page.get('id') === renamePageId
                                );
                            }
                        } catch (findError) {
                            // If findIndex fails, return state unchanged
                            return state;
                        }

                        if (pageIndex !== -1) {
                            try {
                                // Try to update the page name
                                let newState = state.setIn(['data', 'pages', pageIndex, 'name'], newPageName);

                                // Also mirror the change into pagesList.page_titles (if present)
                                try {
                                    const pageTitlesPath = ['pagesList', 'data', 'page_titles'];
                                    const existingPageTitles = newState.getIn(pageTitlesPath);
                                    if (existingPageTitles && typeof existingPageTitles.set === 'function') {
                                        newState = newState.setIn([...pageTitlesPath, renamePageId], newPageName);
                                    }
                                } catch (mirrorErr) {
                                    // If mirroring fails, continue with the updated pages state
                                }

                                return newState;
                            } catch (updateError) {
                                // If updating fails, return state unchanged
                                return state;
                            }
                        }
                    } catch (pageError) {
                        // If page processing fails, return state unchanged
                        return state;
                    }
                } catch (error) {
                    // If anything fails during access to pages, return state unchanged
                    return state;
                }

                // If we get here, we didn't find the page to rename
                return state;
            } catch (outerError) {
                // Last resort fallback for any unexpected errors
                return state;
            }

        case canvasInfoActions.setActivePage:
            try {
                // Check for valid state first
                if (!state || typeof state.getIn !== 'function') {
                    return state;
                }

                try {
                    // Ensure we have at least one page before setting active page
                    let stateWithPages = state;
                    let hasValidPages = false;

                    try {
                        const pages = state.getIn(['data', 'pages']);
                        if (!pages || typeof pages.size !== 'number' || pages.size === 0) {
                            // Need to initialize pages
                            try {
                                stateWithPages = state.setIn(['data', 'pages'], fromJS([]));
                                hasValidPages = true;
                            } catch (initError) {
                                // If initialization fails, return state unchanged
                                return state;
                            }
                        } else {
                            // Pages already exist
                            stateWithPages = state;
                            hasValidPages = true;
                        }
                    } catch (pageCheckError) {
                        // If checking pages fails, rebuild state with default page
                        try {
                            stateWithPages = fromJS({
                                isLoading: state.get('isLoading') || false,
                                hasErrors: state.get('hasErrors') || false,
                                data: {
                                    buyplan_id: state.getIn(['data', 'buyplan_id']) || null,
                                    month: state.getIn(['data', 'month']) || null,
                                    pages: [],
                                    active_page_id: '',
                                    hideProductsOnCanvasMap: state.getIn(['data', 'hideProductsOnCanvasMap']) || {}
                                }
                            });
                            hasValidPages = true;
                        } catch (rebuildError) {
                            // If rebuilding fails, return initial state
                            return initialState;
                        }
                    }

                    if (!hasValidPages) {
                        return initialState;
                    }

                    try {
                        // Get the requested page ID to activate
                        const activePageId = action.payload.pageId;
                        let pageExists = false;

                        try {
                            // Check if the requested page exists
                            const pages = stateWithPages.getIn(['data', 'pages']);
                            if (pages && typeof pages.some === 'function') {
                                pageExists = pages.some(page => {
                                    try {
                                        return page && typeof page.get === 'function' &&
                                            page.get('id') === activePageId;
                                    } catch (pageError) {
                                        return false;
                                    }
                                });
                            }
                        } catch (existsError) {
                            // If checking existence fails, default to not existing
                            pageExists = false;
                        }

                        // If page doesn't exist, use empty string (no default)
                        const finalPageId = pageExists ? activePageId : '';

                        try {
                            // Try to update the active page ID
                            return stateWithPages.setIn(['data', 'active_page_id'], finalPageId);
                        } catch (updateError) {
                            // If updating fails, return state unchanged
                            return stateWithPages;
                        }
                    } catch (activationError) {
                        // If page activation process fails, return state with pages
                        return stateWithPages;
                    }
                } catch (innerError) {
                    // If anything fails in the main process, return state unchanged
                    return state;
                }
            } catch (outerError) {
                // Last resort fallback for any unexpected errors
                return state;
            }

        case canvasInfoActions.updatePageCanvas:
            try {
                const { pageId: pageToUpdate, canvasData, pageTitle } = action.payload;

                // Check for valid state first
                if (!state || typeof state.getIn !== 'function') {
                    return state;
                }

                try {
                    const pagesForUpdate = state.getIn(['data', 'pages']);
                    if (!pagesForUpdate || typeof pagesForUpdate.size !== 'number' || pagesForUpdate.size === 0) {
                        return state;
                    }

                    // Find the target page index with safety checks
                    let targetPageIndex = -1;

                    try {
                        if (typeof pagesForUpdate.findIndex === 'function') {
                            targetPageIndex = pagesForUpdate.findIndex(
                                page => page && typeof page.get === 'function' && page.get('id') === pageToUpdate
                            );
                        }
                    } catch (findError) {
                        // If finding the page fails, return state unchanged
                        return state;
                    }

                    if (targetPageIndex !== -1) {
                        try {
                            // Safely update the canvas state
                            let updatedState;
                            try {
                                updatedState = state.setIn(
                                    ['data', 'pages', targetPageIndex, 'canvas_state'],
                                    fromJS(canvasData)
                                );
                            } catch (updateCanvasError) {
                                // If updating canvas state fails, return state unchanged
                                return state;
                            }

                            // If pageTitle is provided, also update the page name
                            if (pageTitle) {
                                try {
                                    updatedState = updatedState.setIn(
                                        ['data', 'pages', targetPageIndex, 'name'],
                                        pageTitle
                                    );
                                } catch (updateNameError) {
                                    // If updating name fails, return state with just canvas updated
                                    return updatedState;
                                }
                            }

                            return updatedState;
                        } catch (updateError) {
                            // If updating fails, return state unchanged
                            return state;
                        }
                    }
                } catch (pagesError) {
                    // If accessing pages fails, return state unchanged
                    return state;
                }

                // If we get here, we didn't find the page to update
                return state;
            } catch (outerError) {
                // Last resort fallback for any unexpected errors
                return state;
            }

        case canvasInfoActions.toggleHideProductsOnCanvas:
            try {
                // Check for valid state first
                if (!state || typeof state.getIn !== 'function') {
                    return state;
                }

                try {
                    // Store as global flag instead of per-page map
                    return state.setIn(['data', 'hideProductsOnCanvas'], action.payload.isHidden);
                } catch (setError) {
                    // If setting the flag fails, try a full state rebuild
                    try {
                        return fromJS({
                            isLoading: state.get('isLoading') || false,
                            hasErrors: state.get('hasErrors') || false,
                            data: {
                                buyplan_id: state.getIn(['data', 'buyplan_id']) || null,
                                month: state.getIn(['data', 'month']) || null,
                                pages: state.getIn(['data', 'pages']) ?
                                    state.getIn(['data', 'pages']).toJS() :
                                    [],
                                active_page_id: state.getIn(['data', 'active_page_id']) || '',
                                hideProductsOnCanvas: action.payload.isHidden
                            }
                        });
                    } catch (rebuildError) {
                        // If all else fails, return state unchanged
                        return state;
                    }
                }
            } catch (outerError) {
                // Last resort fallback for any unexpected errors
                return state;
            }

        case canvasInfoActions.requestPages:
            return state.mergeIn(['pagesList'], {
                isLoading: true,
                hasErrors: false
            });

        case canvasInfoActions.successPages:
            const pageData = action.payload.data || {};
            const pageTitles = pageData.page_titles || {};
            const canvasState = pageData.canvas_state;
            const currentPageId = pageData.current_page_id;

            // Debug logging for SUGGESTED ITEMS
            if (pageData.month && (pageData.month.includes('SUGGESTED') || pageData.month.includes('SUGGESTED'))) {
                // eslint-disable-next-line no-console
                console.log('[canvasInfoReducer] SUGGESTED ITEMS Debug:', {
                    month: pageData.month,
                    pageTitles,
                    totalPages: pageData.total_pages,
                    actionPayload: action.payload
                });
            }

            // Filter out page0 and create sorted list of valid pages
            const validPages = Object.entries(pageTitles)
                .filter(([pageId, title]) => pageId !== 'page0' && title !== null)
                .sort(([idA], [idB]) => {
                    const numA = parseInt(idA.replace('page', ''), 10);
                    const numB = parseInt(idB.replace('page', ''), 10);
                    return numA - numB;
                })
                .reduce((acc, [pageId, title]) => {
                    acc[pageId] = title;
                    return acc;
                }, {});

            let updatedState = state.mergeIn(['pagesList'], {
                isLoading: false,
                hasErrors: false,
                data: fromJS({
                    month: pageData.month,
                    total_pages: pageData.total_pages,
                    page_titles: validPages
                })
            });

            // CRITICAL FIX: Filter the main pages array to only include pages for this month
            const validPageIds = new Set(Object.keys(validPages));
            const existingPages = updatedState.getIn(['data', 'pages']) || fromJS([]);
            const filteredPages = existingPages.filter(page => {
                if (!page || !page.get) return false;
                const pageId = page.get('id');
                return validPageIds.has(pageId);
            });

            // Update the main pages array with filtered pages
            updatedState = updatedState.setIn(['data', 'pages'], filteredPages);

            // If canvas_state and current_page_id are provided, update the canvas state for that page
            if (canvasState && currentPageId) {
                try {
                    const pages = updatedState.getIn(['data', 'pages']);
                    if (pages && typeof pages.findIndex === 'function') {
                        const pageIndex = pages.findIndex(page => 
                            page && typeof page.get === 'function' && page.get('id') === currentPageId
                        );
                        
                        if (pageIndex !== -1) {
                            updatedState = updatedState.setIn(
                                ['data', 'pages', pageIndex, 'canvas_state'],
                                fromJS(canvasState)
                            );
                        }
                    }
                } catch (error) {
                  // If updating canvas state fails, just return the state with pages updated
                }
            }

            return updatedState;

        case canvasInfoActions.failurePages:
            return state.mergeIn(['pagesList'], {
                isLoading: false,
                hasErrors: true,
                data: fromJS([])
            });

        // Canvas state with all products cases - store separately to avoid main canvas re-renders
        case canvasInfoActions.startCanvasStateAllLoader:
            return state.setIn(["canvasStateAll", "isLoading"], true);

        case canvasInfoActions.successCanvasStateAll:
            return state.merge({
                canvasStateAll: {
                    isLoading: false,
                    hasErrors: false,
                    data: fromJS(action.payload.data)
                }
            });

        case canvasInfoActions.failureCanvasStateAll:
            return state.merge({
                canvasStateAll: {
                    isLoading: false,
                    hasErrors: true,
                    data: fromJS(action.payload.data || {})
                }
            });

        default:
            return state;
    }
};
